import React, { Fragment, useCallback, useMemo, useRef, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import { BUSINESS_UNIT } from '@zola-helpers/client/dist/es/tracking/onboard/onboardTrackingConstants';
import { UploadCareTabTypes } from '@zola-helpers/client/dist/es/uploadcare/constants';

import { trackEvent } from '@zola-helpers/client/dist/es/tracking/trackingHelper';

import { CaretIcon } from '@zola/zola-ui/src/components/SvgIcons';

import { getPLPPhotoPreviewState } from 'cards/selectors/plpPhotoPreview';
import { updatePlpPhotoPreviewState } from 'cards/actions/plpPhotoPreview';
import { getCardCatalogBusy, getSearchFilters } from 'cards/selectors/cardCatalogSelector';

import { openUploadcareModal } from 'components/common/UploadcareWidget';

import { useSessionStorageData } from 'cards/components/CardListing/PhotoPreview/hook';

import {
  ButtonBox,
  DropdownBox,
  PhotoOption,
  SwapIcon,
  RemoveIcon,
  PreviewIcon,
  PhotoIcon,
  StyledButton,
} from './styles';
import {
  PLP_ADD_LIVE_PHOTO,
  PLP_PREVIEW_LIVE_PHOTO,
  PLP_SWAP_LIVE_PHOTO,
  ZOLA_PLP_PHOTO_PREVIEW,
} from '../constants';

/* eslint-disable  @typescript-eslint/no-explicit-any */

export interface UploadProps {
  isSimpleButton?: boolean;
  updateFilter: (key: string, value: any) => void;
}

const Upload = ({ isSimpleButton, updateFilter }: UploadProps): JSX.Element | null => {
  const dispatch = useDispatch();

  const searchFilters = useSelector(getSearchFilters);
  const cardCatalogBusy = useSelector(getCardCatalogBusy);
  const { digital_suite: isDigital, lead_card_type: cardType } = searchFilters;

  const plpPhotoPreviewState = useSelector(getPLPPhotoPreviewState);
  const { currentFlowUuid, currentUploadcareFile, resetSessionData } = useSessionStorageData(
    cardType,
    isDigital
  );

  const [showOptions, setShowOptions] = useState(false);

  // TODO: Move this into useState or something, need to make sure it updates properly to be used in the tracking flow though
  const trackingParamsRef = useRef<{ flowUuid: string | null; isFromWalkthrough: boolean }>({
    flowUuid: null,
    isFromWalkthrough: false,
  });

  const resolvedCardType = useMemo(() => cardType && `${isDigital ? 'DIGITAL_' : ''}${cardType}`, [
    cardType,
    isDigital,
  ]);

  useEffect(() => {
    const handler = (event: any) => {
      event?.stopPropagation();
      event?.nativeEvent?.stopImmediatePropagation();
      setShowOptions(false);
    };

    window.addEventListener('click', handler);
    return () => {
      window.removeEventListener('click', handler);
    };
  }, []);

  const [filterInitialized, setFilterInitialized] = useState(false);
  useEffect(() => {
    if (cardCatalogBusy || filterInitialized) {
      return;
    }

    if (plpPhotoPreviewState?.uploadcareFile || currentUploadcareFile) {
      updateFilter('has_custom_photo', true);
      setFilterInitialized(true);
    }
  }, [
    filterInitialized,
    plpPhotoPreviewState,
    currentUploadcareFile,
    cardCatalogBusy,
    updateFilter,
  ]);

  const Dropdown = useCallback(({ onSwap, onDelete }) => {
    return (
      <DropdownBox
        onClick={event => {
          event?.stopPropagation();
          event?.nativeEvent?.stopImmediatePropagation();
        }}
      >
        <PhotoOption onClick={onSwap}>
          <SwapIcon /> Swap
        </PhotoOption>
        <PhotoOption onClick={onDelete}>
          <RemoveIcon /> Delete
        </PhotoOption>
      </DropdownBox>
    );
  }, []);

  const handleShowOptions = useCallback((show, event) => {
    event?.stopPropagation();
    event?.nativeEvent?.stopImmediatePropagation();
    setShowOptions(show);
  }, []);

  const getTrackingData = useCallback(
    (isSwapping = false) => {
      let entryPoint = PLP_PREVIEW_LIVE_PHOTO;
      if (trackingParamsRef.current.isFromWalkthrough) {
        entryPoint = PLP_ADD_LIVE_PHOTO;
      } else if (isSwapping) {
        entryPoint = PLP_SWAP_LIVE_PHOTO;
      }

      return {
        entry_point: entryPoint,
        flow_run_id: trackingParamsRef.current.flowUuid,
        business_unit: BUSINESS_UNIT.INVITATIONS,
        category: resolvedCardType,
      };
    },
    [resolvedCardType, trackingParamsRef]
  );

  const onSave = useCallback(
    (uploadcareFile, isSwapping = false) => {
      if (resolvedCardType) {
        const sessionData = JSON.parse(
          window.sessionStorage.getItem(ZOLA_PLP_PHOTO_PREVIEW) || '{}'
        );

        const {
          cdnUrl,
          mimeType,
          cdnUrlModifiers,
          crop,
          originalImageInfo,
          originalUrl,
        } = uploadcareFile;
        window.sessionStorage.setItem(
          ZOLA_PLP_PHOTO_PREVIEW,
          JSON.stringify({
            ...sessionData,
            [resolvedCardType]: {
              uploadcareFile: {
                cdnUrl,
                mimeType,
                cdnUrlModifiers,
                crop,
                originalImageInfo,
                originalUrl,
              },
              flowUuid: trackingParamsRef.current.flowUuid,
            },
          })
        );
        dispatch(
          updatePlpPhotoPreviewState({ cardType: resolvedCardType || null, uploadcareFile })
        );
        updateFilter('has_custom_photo', true);
      }
      trackEvent('Flow Completed', getTrackingData(isSwapping));
      trackEvent('Product List Viewed', getTrackingData(isSwapping));
    },
    [dispatch, resolvedCardType, getTrackingData, updateFilter]
  );

  const getUploadcareModalOptions = useCallback(
    (isSwapping = false) => ({
      effects: 'crop,mirror,enhance,sharp,blur,grayscale',
      onUploadcareFileReady: (uploadcareFile: any) => onSave(uploadcareFile, isSwapping),
      analyticsLocation: '',
      analyticsProduct: 'CARDS',
    }),
    [onSave]
  );

  const startUcFlow = useCallback(
    (editImageUrl?: string, isSwapping = false) => {
      /**
       * The following line sets the flow UUID for PLP photo upload for a specific card type.
       * The flow UUID stays the same until the PLP preview photo is deleted.
       */
      trackingParamsRef.current.flowUuid =
        trackingParamsRef.current.flowUuid || currentFlowUuid || uuidv4();
      trackingParamsRef.current.isFromWalkthrough = false;
      trackEvent('Flow Started', getTrackingData(isSwapping));

      openUploadcareModal({
        ...getUploadcareModalOptions(isSwapping),
        editImageUrl,
        additionalUploadedFilesTrackingProperties: {
          flow_run_id: trackingParamsRef.current.flowUuid,
        },
        additionalUploadEventTrackingProperties: {
          flow_run_id: trackingParamsRef.current.flowUuid,
        },
        tabs: [
          UploadCareTabTypes.FILE,
          UploadCareTabTypes.RECENT,
          UploadCareTabTypes.DROPBOX,
          UploadCareTabTypes.GOOGLE_PHOTOS,
        ],
      });
    },
    [trackingParamsRef, getUploadcareModalOptions, getTrackingData, currentFlowUuid]
  );

  const handleButtonClick = (e: any) => {
    if (plpPhotoPreviewState?.uploadcareFile || currentUploadcareFile) {
      handleShowOptions(true, e);
    } else {
      startUcFlow();
    }
  };

  const handleDelete = () => {
    dispatch(updatePlpPhotoPreviewState({ uploadcareFile: null }));
    resetSessionData();
    updateFilter('has_custom_photo', false);

    trackingParamsRef.current.flowUuid = null;

    if (resolvedCardType) {
      const { [resolvedCardType]: ignored, ...sessionData } = JSON.parse(
        window.sessionStorage.getItem(ZOLA_PLP_PHOTO_PREVIEW) || '{}'
      );
      window.sessionStorage.setItem(ZOLA_PLP_PHOTO_PREVIEW, JSON.stringify(sessionData));
    }

    setShowOptions(false);
  };

  const handleSwap = () => {
    const editImageUrl =
      plpPhotoPreviewState?.uploadcareFile?.cdnUrl || currentUploadcareFile?.cdnUrl;
    startUcFlow(editImageUrl, true);
  };

  if (!plpPhotoPreviewState) {
    return null;
  }

  const handleSimpleButtonClick = () => {
    if (plpPhotoPreviewState?.uploadcareFile || currentUploadcareFile) {
      handleDelete();
    } else {
      startUcFlow();
    }
  };

  return (
    <React.Fragment>
      {isSimpleButton ? (
        <StyledButton
          id="preview-photo-walkthrough"
          fullWidth
          size="large"
          onClick={handleSimpleButtonClick}
        >
          {plpPhotoPreviewState?.uploadcareFile || currentUploadcareFile ? (
            <>
              <PreviewIcon
                src={plpPhotoPreviewState?.uploadcareFile?.cdnUrl || currentUploadcareFile.cdnUrl}
                alt="preview"
              />
              Clear preview photo
            </>
          ) : (
            <>
              <PhotoIcon width={24} height={24} />
              Use my photo
            </>
          )}
        </StyledButton>
      ) : (
        <ButtonBox>
          <StyledButton
            id="preview-photo-walkthrough"
            fullWidth
            onClick={(e: any) => handleButtonClick(e)}
          >
            {plpPhotoPreviewState?.uploadcareFile || currentUploadcareFile ? (
              <Fragment>
                Photo options <CaretIcon />
              </Fragment>
            ) : (
              <Fragment>Preview with photo</Fragment>
            )}
          </StyledButton>

          {showOptions && <Dropdown onSwap={handleSwap} onDelete={handleDelete} />}
        </ButtonBox>
      )}
    </React.Fragment>
  );
};

export default Upload;
